// src/services/api/wallet/useTalentWallet.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSnackbar } from 'notistack';
const fallbackData = {
  earnedChips: 3790,
  bookings: [
    { 
      date: '2025-06-25',
      session: 'Mock Session A',
      client: 'demo_user_1',
      chips: 120,
    },
    {
      date: '2025-06-23',
      session: 'Practice Session',
      client: 'demo_user_2',
      chips: 150,
    },
     {
      date: '2025-06-25',
      session: 'Mock Session A',
      client: 'demo_user_1',
      chips: 170,
    },
    {
      date: '2025-06-23',
      session: 'Practice Session',
      client: 'demo_user_2',
      chips: 180,
    },
     {
      date: '2025-06-25',
      session: 'Mock Session A',
      client: 'demo_user_1',
      chips: 190,
    },
    {
      date: '2025-06-23',
      session: 'Practice Session',
      client: 'demo_user_2',
      chips: 280,
    },
     {
      date: '2025-06-25',
      session: 'Mock Session A',
      client: 'demo_user_1',
      chips: 320,
    },
    {
      date: '2025-06-23',
      session: 'Practice Session',
      client: 'demo_user_2',
      chips: 180,
    },
     {
      date: '2025-06-25',
      session: 'Mock Session A',
      client: 'demo_user_1',
      chips: 420,
    },
    {
      date: '2025-06-23',
      session: 'Practice Session',
      client: 'demo_user_2',
      chips: 480,
    },
     {
      date: '2025-06-25',
      session: 'Mock Session A',
      client: 'demo_user_1',
      chips: 520,
    },
    {
      date: '2025-06-23',
      session: 'Practice Session',
      client: 'demo_user_2',
      chips: 780,
    }
  ],
};

// Utility: Axios call with 3-second timeout fallback
const fetchWithTimeout = (url: string, timeout = 3000) => {
  return Promise.race([
    axios.get(url),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('⏱ Timeout after 3s')), timeout)
    ),
  ]);
};

export const useTalentWallet = (talentId: string) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const walletQuery = useQuery({
    queryKey: ['talentWallet', talentId],
    queryFn: async () => {
      try {
        const response = await fetchWithTimeout(`/api/talents/${talentId}/wallet`, 3000);
        const data = (response as any).data;

        if (!data || typeof data.earnedChips !== 'number' || !Array.isArray(data.bookings)) {
          console.warn('⚠️ Incomplete or invalid data from API. Using fallback.');
          return fallbackData;
        }

        return data;
      } catch (err) {
        console.error('❌ Failed to fetch wallet data. Using fallback.', err);
        return fallbackData;
      }
    },
    staleTime: 60 * 1000,
  });

 const syncMutation = useMutation({
    mutationFn: (amount?: number) =>
      axios.post(`/api/talents/${talentId}/wallet/sync`, { amount }),

    onSuccess: (_, amount) => {
      queryClient.invalidateQueries(['talentWallet', talentId]);

      // enqueueSnackbar(
      //   `✅ Successfully transferred ${amount?.toLocaleString() || 0} chips to your main wallet.`,
      //   { variant: 'success' }
      // );
    },

    onError: (error: any, amount) => {
      const message =
        error?.response?.data?.message ||
        `❌ Failed to transfer ${amount?.toLocaleString() || 0} chips. Please try again.`;

      // enqueueSnackbar(message, { variant: 'error' });
    },
  });

  // ✅ Safe and async wrapper for external use
   // ✅ Add explicit return type here
  const onSync = async (amount?: number): Promise<{ success: boolean; message?: string }> => {
    try {
      await syncMutation.mutateAsync(amount);
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || 'An unexpected error occurred.',
      };
    }
  };

  return {
    ...walletQuery,
    syncToMainWallet: syncMutation.mutate, // direct use if needed
    onSync, // robust function for use in UI
  };
};
